import { CommonModule } from '@angular/common';
import { Component, Inject, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { COMPANY_PROFILE_CARD_DATA_TOKEN } from '@galaxy/crm/static';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SubscriptionList } from '@vendasta/rx-utils';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, ReplaySubject, startWith } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import {
  CreateSnapshotRequest,
  CurrentSnapshot,
  SnapshotService,
  SnapshotUrl,
} from '../../../../core/snapshot.service';
import { CompanyAccountService } from '../../../company-account.service';
import { AssociationPanelModule } from '../association-panel.module';
import { BusinessCenterService } from '../../../../core/business-center.service';
import { ExecutiveReportSenderComponent } from '../../../../businesses/details/views/overview/components/executive-report-sender/executive-report-sender.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-reports',
  imports: [
    CommonModule,
    MatButtonModule,
    MatExpansionModule,
    MatCardModule,
    MatIconModule,
    GalaxyBadgeModule,
    MatMenuModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    AssociationPanelModule,
  ],
  templateUrl: './reports.component.html',
  styleUrls: ['../association-panel-base-styles.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {
  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);

  private readonly snapshotLookupId$$ = new ReplaySubject<string>(1);
  private readonly snapshotLookupId$ = this.snapshotLookupId$$.pipe(take(1));

  private readonly refreshingSnapshot$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly refreshingSnapshot$ = this.refreshingSnapshot$$.asObservable();

  private snapshotUrl$: Observable<SnapshotUrl>;

  snapshotName$: Observable<string>;
  snapshot$: Observable<CurrentSnapshot>;

  snapshotCardReady$: Observable<boolean>;
  reportCardsReady$: Observable<boolean>;
  creatingSnapshot$: Observable<boolean>;
  snapshotLoading$: Observable<boolean>;

  now = new Date();

  private readonly subscriptions = SubscriptionList.new();
  accountGroupId$ = this.accountCreationService.accountGroupId$;
  isAccountGroupLoading = toSignal(this.accountCreationService.createAccountGroupLoading$(this.data.company_id));

  constructor(
    private readonly snapshotService: SnapshotService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    private readonly accountCreationService: CompanyAccountService,
    private readonly businessCenterService: BusinessCenterService,
    public dialog: MatDialog,
    private reportSenderDialog: MatDialogRef<ExecutiveReportSenderComponent>,
  ) {}

  ngOnInit(): void {
    this.setupAccountGroupIdFetching();
    this.setupSnapshotFetching();
    this.reportCardsReady$ = combineLatest([this.snapshotCardReady$]).pipe(map((_) => true));
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  private setupAccountGroupIdFetching(): void {
    this.accountCreationService.setAccountGroupId(this.data.account_group_id ?? '');
    this.snapshotLookupId$$.next(this.data.account_group_id ?? '');
  }

  private setupSnapshotFetching(): void {
    this.subscriptions.add(this.accountGroupId$, (id) => {
      this.snapshotService.getCurrentSnapshotV2$(id);
    });
    this.subscriptions.add(this.snapshotLookupId$, (id) => {
      this.snapshotService.fetchSnapshotName(id);
    });

    const refresh$ = combineLatest([this.partnerId$, this.accountGroupId$, this.refreshingSnapshot$]).pipe(
      filter(([partnerId, accountGroupId, refreshing]) => !!partnerId && !!accountGroupId && refreshing),
      map(
        ([partnerId, accountGroupId]) =>
          ({
            origin: 'company-profile',
            partnerId,
            accountGroupId,
          }) as CreateSnapshotRequest,
      ),
      switchMap((req) => this.snapshotService.refreshCompanySnapshot(req)),
    );

    this.subscriptions.add(refresh$, (refresh) => {
      if (refresh) {
        this.snapshotService.showRefreshSnapshotSuccessMessage();
      } else {
        this.snapshotService.showRefreshSnapshotErrorMessage();
      }
    });

    this.snapshot$ = this.accountGroupId$.pipe(switchMap((id) => this.snapshotService.getCurrentSnapshotUpdates$(id)));

    this.snapshotName$ = this.snapshotLookupId$.pipe(switchMap((id) => this.snapshotService.getSnapshotName$(id)));
    this.snapshotUrl$ = this.snapshot$.pipe(
      switchMap((snapshot) => this.snapshotService.getSnapshotUrl$(snapshot.snapshotID)),
      shareReplay(1),
    );

    const snapshotReady$ = this.accountGroupId$.pipe(
      switchMap((id) => this.snapshotService.currentSnapshotLoading$(id)),
      filter((loading) => loading === false),
    );

    this.snapshotLoading$ = this.accountGroupId$.pipe(
      switchMap((id) => this.snapshotService.currentSnapshotLoading$(id)),
    );

    this.snapshotCardReady$ = combineLatest([snapshotReady$, this.snapshotName$]).pipe(
      map(() => true),
      startWith(false),
    );

    const createSnapshotLoading$ = this.accountGroupId$.pipe(
      distinctUntilChanged(),
      switchMap((id) => this.snapshotService.createSnapshotLoading$(id)),
    );
    this.creatingSnapshot$ = combineLatest([
      this.accountCreationService.createAccountGroupLoading$(this.data.company_id),
      createSnapshotLoading$,
    ]).pipe(map(([accountGroup, snapshot]) => accountGroup || snapshot));
  }

  private openSnapshot(url: string) {
    window.open(url, '_blank', 'noopener');
  }

  async navToViewSnapshot(): Promise<void> {
    await firstValueFrom(this.snapshotUrl$).then(
      (url: SnapshotUrl) => this.openSnapshot(url.view),
      (_: Error) => {
        const err = this.translate.instant(
          'CRM.ASSOCIATION_PANEL.REPORTS.SNAPSHOT_REPORT_CARD.ERRORS.UNABLE_TO_GET_URL',
        );
        this.snackbarService.openErrorSnack(err);
      },
    );
  }

  async navToEditSnapshot(): Promise<void> {
    await firstValueFrom(this.snapshotUrl$).then(
      (url: SnapshotUrl) => this.openSnapshot(url.edit),
      (_: Error) => {
        const err = this.translate.instant(
          'CRM.ASSOCIATION_PANEL.REPORTS.SNAPSHOT_REPORT_CARD.ERRORS.UNABLE_TO_GET_URL',
        );
        this.snackbarService.openErrorSnack(err);
      },
    );
  }

  refreshSnapshot(): void {
    this.refreshingSnapshot$$.next(true);
  }

  createSnapshot(): void {
    const accountGroupId$ = this.accountCreationService.fetchAccountGroupId$(this.data.company_id);
    const work$ = combineLatest([this.partnerId$, accountGroupId$]).pipe(
      map(([partnerId, accountGroupId]) => {
        if (accountGroupId) {
          return {
            origin: 'company-profile',
            partnerId,
            accountGroupId,
          } as CreateSnapshotRequest;
        }
        throw null;
      }),
      switchMap((req) => this.snapshotService.createCompanySnapshot(req)),
    );
    work$.pipe(take(1)).subscribe({
      next: (snapshotId) => {
        if (snapshotId) {
          this.snapshotService.showCreateSnapshotSuccessMessage();
        }
      },
      error: () => this.snapshotService.showCreateSnapshotErrorMessage(),
    });
  }

  createAccount() {
    if (this.data.account_group_id) {
      this.accountCreationService.setAccountGroupId(this.data.account_group_id);
      return;
    }
    const companyId = this.data.company_id;
    firstValueFrom(this.accountCreationService.fetchAccountGroupId$(companyId)).then((accountGroupId) => {
      if (accountGroupId) {
        this.accountCreationService.setAccountGroupId(accountGroupId);
      } else {
        this.snackbarService.openErrorSnack('CRM.ERROR.ACCOUNT_GROUP_CREATION_FAILED');
      }
    });
  }

  openExecReport(adminView: boolean) {
    this.accountGroupId$
      .pipe(
        switchMap((accountGroupId) =>
          this.businessCenterService.getVBCEntryUrl(accountGroupId, 'executive-report/monthly'),
        ),
        map((entryInfo) => {
          if (adminView) {
            return entryInfo.url + '?showReviewGradesCard=true';
          }
          return entryInfo.url;
        }),
      )
      .subscribe({
        next: (url) => {
          window.open(url, '_blank');
        },
      });
  }

  openExecReportSenderDialog(): void {
    combineLatest([this.accountGroupId$, this.partnerId$])
      .pipe(
        switchMap(([accountGroupId, partnerId]) => {
          this.reportSenderDialog = this.dialog.open(
            ExecutiveReportSenderComponent,
            ExecutiveReportSenderComponent.config(accountGroupId, partnerId, this.data.name),
          );
          return this.reportSenderDialog.afterClosed();
        }),
        take(1),
      )
      .subscribe();
  }
}
